'use client';

import { ForwardedRef, forwardRef, Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';

import { Action, Spacing } from '~/components';
import { Button, Typography, theme, IcChevronRight, IcAccept } from '@bgzt/ui';

import { FilterValue } from '~/apis/search';
import { findBreadcrumb } from '~/services/category';

import styles from './CategorySelection.module.css';
import globalStyles from '~/styles/utils.module.css';

type Props = {
  onChange: (action: Action) => void;
  options: FilterValue[];
  categoryId?: string;
  filterKey: string;
  title: string;
};

export const CategorySelection = forwardRef(function CategoryList(
  { categoryId, filterKey, title, options, onChange }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryHistory = useMemo(() => {
    return findBreadcrumb(categoryId, options);
  }, [options, categoryId]);

  const currentOptions = useMemo(() => {
    return categoryHistory[categoryHistory.length - 1]?.categories ?? [];
  }, [categoryHistory]);

  const showExpandButton = currentOptions.length > 5 && !isExpanded;

  const handleMenuButtonClick = (categoryId: string) => {
    onChange({ type: filterKey, payload: categoryId });
  };

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.titleContainer}>
        <Typography variant="L7" color="gray-200">
          {title}
        </Typography>
      </div>
      <div className={clsx(styles.breadCrumbContainer, globalStyles.scrollXHidden)}>
        <Typography
          variant="L4"
          color={categoryHistory[0]?.name ? 'gray-900' : 'red-500'}
          className={styles.breadCrumb}
          onClick={() => {
            onChange({ type: filterKey, payload: '' });
          }}
        >
          전체
        </Typography>
        {categoryHistory.map(({ name, value }) => (
          <Fragment key={value}>
            {name && (
              <IcChevronRight
                size={16}
                fill={theme.colors['gray-200']}
                className={clsx([styles.breadCrumb, styles.breadCrumbIcon])}
              />
            )}
            <Typography
              variant="L4"
              color={categoryHistory.findLast(({ name }) => name)?.name === name ? 'red-500' : 'gray-900'}
              onClick={() => {
                onChange({ type: filterKey, payload: value });
              }}
              className={styles.breadCrumb}
            >
              {name}
            </Typography>
          </Fragment>
        ))}
      </div>
      <div className={styles.menuContainer}>
        {(currentOptions.length <= 5
          ? currentOptions
          : currentOptions.slice(0, isExpanded ? currentOptions.length : 5)
        ).map(({ value, count, name }) => {
          const isSelected = categoryId === value;
          return (
            <button
              className={clsx([
                styles.menuButton,
                !isSelected && styles.multiSelection,
                isSelected && styles.menuButtonSelected,
              ])}
              key={value}
              onClick={() => value && handleMenuButtonClick(value)}
            >
              <div className={styles.categoryMenuTextContainer}>
                <Typography variant="L4" color="gray-900">
                  {name}
                </Typography>
                <Typography variant="L11" color="gray-200">
                  {count?.toLocaleString()}개
                </Typography>
              </div>

              <IcAccept size={20} fill={isSelected ? theme.colors['red-500'] : theme.colors['gray-100']} />
            </button>
          );
        })}
        {showExpandButton && (
          <>
            <Spacing height={10} />
            <Button
              className={styles.moreButton}
              variant="normalGray"
              size="large"
              onClick={() => {
                setIsExpanded(true);
              }}
            >
              더보기
            </Button>
          </>
        )}
      </div>
    </div>
  );
});
